import React from 'react';
import { useTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement } from 'chart.js';
import '../styles/staking-supply.css';
import stakingData, { PRESALE_START_DATE_DISPLAY, TEST_MODE } from '../config/supplyConfig';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement
);

// Función para traducir el mes y mantener el año
const translateMonth = (monthYear, t) => {
  if (!monthYear) return "";
  
  const [month, year] = monthYear.split(' ');
  
  // Mapear los meses en inglés a las claves de traducción
  const monthTranslationMap = {
    'Jan': 'months.jan',
    'Feb': 'months.feb',
    'Mar': 'months.mar',
    'Apr': 'months.apr',
    'May': 'months.may',
    'Jun': 'months.jun',
    'Jul': 'months.jul',
    'Aug': 'months.aug',
    'Sep': 'months.sep',
    'Oct': 'months.oct',
    'Nov': 'months.nov',
    'Dec': 'months.dec'
  };
  
  // Obtener la traducción del mes
  const translatedMonth = t(monthTranslationMap[month] || month);
  
  // Devolver el mes traducido junto con el año
  return `${translatedMonth} ${year}`;
};

// Función para extraer el mes y año de la fecha de inicio
const extractMonthYear = (dateString, t) => {
  const [datePart] = dateString.split(' ');
  const [day, month, year] = datePart.split('-');
  
  // Crear un objeto Date
  const date = new Date(year, parseInt(month) - 1, day);
  
  // Obtener el nombre del mes en inglés (para comparar con los datos)
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthName = monthNames[date.getMonth()];
  
  return `${monthName} ${year}`;
};

// Función para obtener el mes y año actual en formato "MMM YYYY"
const getCurrentMonthYear = () => {
  const date = new Date();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthName = monthNames[date.getMonth()];
  return `${monthName} ${date.getFullYear()}`;
};

// Obtenemos el mes activo según el modo (prueba o real)
const activeMonthYear = TEST_MODE ? extractMonthYear(PRESALE_START_DATE_DISPLAY) : getCurrentMonthYear();

// Función para determinar si un mes está en el pasado
const isMonthInPast = (month, activeMonth) => {
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [monthName, year] = month.split(' ');
  const [activeMonthName, activeYear] = activeMonth.split(' ');
  
  const monthIndex = monthNames.indexOf(monthName);
  const activeMonthIndex = monthNames.indexOf(activeMonthName);
  
  if (parseInt(year) < parseInt(activeYear)) {
    return true;
  } else if (parseInt(year) === parseInt(activeYear) && monthIndex < activeMonthIndex) {
    return true;
  }
  
  return false;
};

const StakingSupply = () => {
  const { t } = useTranslation();
  
  const chartData = {
    labels: stakingData.map(item => translateMonth(item.month, t)),
    datasets: [
      {
        label: t('stakingSupply.totalSupply'),
        data: stakingData.map(item => item.amount),
        backgroundColor: stakingData.map(item => {
          if (item.month === activeMonthYear) {
            return '#99cc88'; // Verde oscuro para el mes activo
          } else if (isMonthInPast(item.month, activeMonthYear)) {
            return '#c0e6b4'; // Verde claro para meses pasados
          } else {
            return '#ffffff'; // Blanco para meses futuros
          }
        }),
        borderRadius: {
          topLeft: 5,
          topRight: 5,
          bottomLeft: 0,
          bottomRight: 0
        },
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    scales: {
      x: {
        title: {
          color: '#FFFFFF' // Color del título del eje X (Meses)
        },
        ticks: {
          color: '#FFFFFF' // Color de las etiquetas del eje X (Meses)
        },
        categoryPercentage: 1.0, // Set the bars to take up the entire category width
        barPercentage: 0.9, // Adjust the bar width within the category
        grid: {
          color: 'rgba(255, 255, 255, 0.5)', // Change the color of the X axis grid lines
          lineWidth: 1.5, // Change the thickness of the X axis grid lines
          offset: false, // Ensure grid lines are not offset
        }
      },
      y: {
        title: {
          color: '#FFFFFF' // Color del título del eje Y (Suministro)
        },
        ticks: {
          color: '#FFFFFF', // Color de las etiquetas del eje Y (Cantidades)
          callback: function(value) {
            // Convertir valores a millones con sufijo M
            return value + 'M';
          }
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.5)', // Change the color of the Y axis grid lines
          lineWidth: 1.5  // Change the thickness of the Y axis grid lines
        }
      },
    },
  };

  return (
    <div className="staking-supply">
      <h3 className="total-supply-title">{t('stakingSupply.title')}</h3>
      <div className="chart-container">
        <Bar data={chartData} options={chartOptions} />
      </div>
    </div>
  );
};

export default StakingSupply;
